import Highcharts from 'highcharts';
import { SpendingHistoryDataPoint } from 'src/types/spending';
import { LocalDate } from '@js-joda/core';
import FormatService from 'src/services/format.service';
import LiteralsService from 'src/services/literals/LiteralsService';

const tooltipBackgroundColor = '#F8F9FA';
const tooltipBorderColor = '#C8C9C7';
const tooltipTextColor = '#262626';
const seriesColor = '#006FCF';
const noDataColor = '#53565A';
const xAxisColor = '#ECEDEE';

export function getHistoryBarChartOptions(
    title: string,
    history: SpendingHistoryDataPoint[],
    inModal?: boolean,
    setYAxisLabelWidth?: (yAxisLabelWidth: number) => void,
    setIsChartMobile?: (isChartMobile: boolean) => void,
    isMinWidthMobile?: boolean,
    isMinWidthLaptop?: boolean,
    categoryName?: string
): Highcharts.Options {
    const spendingValues: number[] = history?.map((point) => point.spent);
    /* What width to show the mobile view */
    const mobileWidth: number = 500;
    /* Find the max spent value in the history */
    const maxSpendingValue = Math.max(...spendingValues);
    /* If there is no spending in the last 12 months, set the max value of the y-axis to 6000 */
    const defaultChartMax: number = maxSpendingValue !== 0 ? undefined : 6000;
    /* Get the series data for the chart. If all the values are 0, then we want to return an empty array
     so that the chart will display the "no data" state */
    const seriesData = maxSpendingValue !== 0 ? spendingValues : [];

    return {
        chart: {
            // handle popups when in modal
            style: inModal
                ? {
                      zIndex: '1000'
                  }
                : undefined,
            type: 'column',
            spacingLeft: 0,
            spacingRight: 0,
            spacingBottom: 8,
            backgroundColor: 'transparent',
            marginRight: 5,
            height: 191,
            events: {
                render: function (this: any) {
                    const yAxisLabels = this.yAxis[0].labelGroup.element;
                    setYAxisLabelWidth(yAxisLabels?.getBoundingClientRect().width);
                    setIsChartMobile(this.chartWidth <= mobileWidth); // Tied to the responsive rules section
                }
            }
        },
        title: {
            text: title, // used for ADA screenreaders
            style: {
                display: 'none'
            }
        },
        xAxis: {
            min: 0,
            max: 11,
            categories: history?.map((point) => point.timePeriodStart),
            accessibility: {
                description: 'month'
            },
            lineColor: xAxisColor,
            tickWidth: 0,
            labels: {
                enabled: false
            }
        },
        legend: {
            enabled: false
        },
        yAxis: {
            min: 0,
            title: {
                text: undefined
            },
            labels: {
                formatter: ({ value }: { value: number | string }) =>
                    FormatService.format(typeof value === 'string' ? parseInt(value) : value)
            },
            tickAmount: 4,
            max: defaultChartMax,
            maxPadding: 0.1,
            accessibility: {
                description: 'dollars spent'
            }
        },
        responsive: {
            rules: [
                {
                    condition: {
                        maxWidth: mobileWidth
                    },
                    chartOptions: {
                        yAxis: {
                            labels: {
                                enabled: false
                            }
                        }
                    }
                }
            ]
        },
        tooltip: {
            backgroundColor: tooltipBackgroundColor,
            borderColor: tooltipBorderColor,
            borderRadius: 1,
            borderWidth: 1,
            formatter: function (this: any): string {
                return `<span style="font-size: 12px; font-weight: 500; color: ${tooltipTextColor}; line-height: 18px">
                    <span>${FormatService.formatMonth(LocalDate.parse(this.category))}</span>
                    <br/>
                    <span>${FormatService.formatCurrency(this.y)}</span>
                </span>`;
            },
            positioner: function (
                this: { chart: { plotLeft: number; plotTop: number; chartWidth: number } },
                labelWidth: number,
                labelHeight: number,
                point: { plotX: number; plotY: number }
            ): { x: number; y: number } {
                /**
                 * Get x co-ord
                 * First entry: plotX + plotLeft expression gives midpoint of bar then shifts left by half label width
                 * Second entry: minimum x value so tooltip is not cut off on LHS
                 * Third entry: maximum x value so tooltip is not cut off on RHS
                 * We take the median value of the three entries as our x co-ord
                 */
                const possibleXValues: number[] = [
                    point.plotX + this.chart.plotLeft - labelWidth / 2,
                    inModal ? -14 : -20,
                    this.chart.chartWidth + (inModal ? 13 : 19) - labelWidth
                ];
                const x: number = possibleXValues.sort((a: number, b: number) => a - b)[1];
                /**
                 * Get y co-ord
                 * plotY + plotTop expression gives top of bar then shift up by labelHeight plus 10 to render
                 * pointer arrow
                 */
                const y: number = point.plotY + this.chart.plotTop - (labelHeight + 10);
                return { x, y };
            },
            shadow: false,
            useHTML: true
        },
        series: [
            {
                name: 'Spent',
                pointWidth: isMinWidthMobile ? 24 : 16,
                type: 'column',
                data: seriesData,
                color: seriesColor,
                accessibility: {
                    point: {
                        descriptionFormatter: function ({ x, y, series }: any) {
                            return `Bar graph value of ${FormatService.format(
                                y
                            )} for ${categoryName} spending in ${FormatService.formatAltDateLongMonth(
                                LocalDate.parse(series.points[x].category)
                            )}`;
                        }
                    }
                },
                minPointLength: 3
            }
        ],
        lang: {
            noData: LiteralsService.getLiteral('Budget.History.chartNoData')
        },
        noData: {
            style: {
                fontSize: '14px',
                fontWeight: '400',
                color: noDataColor,
                lineHeight: '20px'
            }
        },
        plotOptions: {
            series: {
                events: {
                    mouseOver: isMinWidthLaptop
                        ? () => {
                              const tooltip = document.querySelector(
                                  'div.highcharts-label.highcharts-tooltip.highcharts-color-undefined'
                              );
                              tooltip?.setAttribute('role', 'complementary');
                          }
                        : undefined,
                    click: !isMinWidthLaptop
                        ? () => {
                              const tooltip = document.querySelector(
                                  'div.highcharts-label.highcharts-tooltip.highcharts-color-undefined'
                              );
                              tooltip?.setAttribute('role', 'complementary');
                          }
                        : undefined
                }
            }
        }
    };
}
